import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useAuth } from '../hooks/useAuth'
import { useSimpleToast } from '../hooks/useToast'
import { Button } from '../components/ui/button'
import { Skeleton } from '../components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog'
import { PlanCard } from '../components/training-plans/PlanCard'
import { trainingPlansService } from '../services/training-plans'
import type { TrainingPlan } from '../types/training-plan'

type TabKey = 'mine' | 'coached' | 'invitations'

export default function TrainingPlansPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showSuccess, showError } = useSimpleToast()

  const [plans, setPlans] = useState<TrainingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<TabKey>('mine')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [planToDelete, setPlanToDelete] = useState<TrainingPlan | null>(null)

  const currentUserId = user ? String(user.id) : ''

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [{ label: t('trainingPlans.pageTitle') }],
    }),
    [t, navigate]
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPlans(await trainingPlansService.list())
    } catch {
      showError(t('trainingPlans.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t, showError])

  useEffect(() => {
    load()
  }, [load])

  const { mine, coached, invitations } = useMemo(() => {
    const mineList: TrainingPlan[] = []
    const coachedList: TrainingPlan[] = []
    const invitationList: TrainingPlan[] = []
    for (const plan of plans) {
      const isAthlete = plan.user.id === currentUserId
      const isCoach = plan.coach?.id === currentUserId
      if (isCoach && plan.status === 'pending') {
        invitationList.push(plan)
      } else if (isCoach) {
        coachedList.push(plan)
      }
      if (isAthlete) mineList.push(plan)
    }
    return { mine: mineList, coached: coachedList, invitations: invitationList }
  }, [plans, currentUserId])

  function replacePlan(updated: TrainingPlan) {
    setPlans((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
  }

  async function handleAccept(plan: TrainingPlan) {
    setBusyId(plan.id)
    try {
      replacePlan(await trainingPlansService.accept(plan.id))
      showSuccess(t('trainingPlans.invitation.accepted'))
      setTab('coached')
    } catch {
      showError(t('trainingPlans.loadError'))
    } finally {
      setBusyId(null)
    }
  }

  async function handleReject(plan: TrainingPlan) {
    setBusyId(plan.id)
    try {
      replacePlan(await trainingPlansService.reject(plan.id))
      showSuccess(t('trainingPlans.invitation.rejected'))
    } catch {
      showError(t('trainingPlans.loadError'))
    } finally {
      setBusyId(null)
    }
  }

  async function confirmDelete() {
    if (!planToDelete) return
    const id = planToDelete.id
    setBusyId(id)
    try {
      await trainingPlansService.remove(id)
      setPlans((prev) => prev.filter((p) => p.id !== id))
      showSuccess(t('trainingPlans.actions.deleted'))
    } catch {
      showError(t('trainingPlans.loadError'))
    } finally {
      setBusyId(null)
      setPlanToDelete(null)
    }
  }

  function renderList(list: TrainingPlan[], emptyKey: string) {
    if (list.length === 0) {
      return <p className="text-sm text-muted-foreground text-center py-12">{t(emptyKey)}</p>
    }
    return (
      <div className="flex flex-col gap-3">
        {list.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currentUserId={currentUserId}
            busy={busyId === plan.id}
            onOpen={(p) => navigate(`/training-plans/${p.id}`)}
            onDelete={(p) => setPlanToDelete(p)}
            onAccept={handleAccept}
            onReject={handleReject}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('trainingPlans.pageTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('trainingPlans.subtitle')}</p>
        </div>
        <Button onClick={() => navigate('/training-plans/new')} className="shrink-0">
          <Plus className="size-4" />
          <span className="hidden sm:inline">{t('trainingPlans.newPlan')}</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="mine">{t('trainingPlans.tabs.mine')}</TabsTrigger>
            <TabsTrigger value="coached">{t('trainingPlans.tabs.coached')}</TabsTrigger>
            <TabsTrigger value="invitations">
              {t('trainingPlans.tabs.invitations')}
              {invitations.length > 0 && (
                <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
                  {invitations.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-4">
            {renderList(mine, 'trainingPlans.empty.mine')}
          </TabsContent>
          <TabsContent value="coached" className="mt-4">
            {renderList(coached, 'trainingPlans.empty.coached')}
          </TabsContent>
          <TabsContent value="invitations" className="mt-4">
            {renderList(invitations, 'trainingPlans.empty.invitations')}
          </TabsContent>
        </Tabs>
      )}

      <AlertDialog open={planToDelete !== null} onOpenChange={(open) => !open && setPlanToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('trainingPlans.actions.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('trainingPlans.actions.deleteConfirmBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                confirmDelete()
              }}
            >
              {t('trainingPlans.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
